import React, { useMemo } from 'react';
import { Image } from 'antd';
import { useTranslation } from 'react-i18next';
import type { NonCashOrderPayment } from 'apis/fms';
import type { FmsAccountManagement } from 'types/fms';
import LOCALS from 'commons/locals';
import PrivateReceiptImage from 'components/private-receipt-image';

type PaymentRecordsCardProps = {
  titleLabel: string;
  payments?: NonCashOrderPayment[];
  paymentAccountList: FmsAccountManagement[];
  currencySymbol: string;
  formatAmount: (amount: number | null | undefined) => string;
  totalLabel?: string;
  emptyLabel?: string;
  totalContainerClassName?: string;
  totalAmountClassName?: string;
  className?: string;
};

const PaymentRecordsCard: React.FC<PaymentRecordsCardProps> = ({
  titleLabel,
  payments = [],
  paymentAccountList,
  currencySymbol,
  formatAmount,
  totalLabel = LOCALS.daily_register_close_cashless_sales_total,
  emptyLabel = LOCALS.daily_register_close_no_cashless_sales_today,
  totalContainerClassName = 'bg-blue-50 border-blue-200',
  totalAmountClassName = 'text-blue-600',
  className = '',
}) => {
  const { t } = useTranslation();
  const accountNameByCode = useMemo(() => {
    return paymentAccountList.reduce<Record<string, string>>(
      (accountMap, account) => ({
        ...accountMap,
        [account.accountCode]: account.accountName,
      }),
      {},
    );
  }, [paymentAccountList]);
  const totalAmount = useMemo(
    () =>
      payments.reduce(
        (total: number, payment: NonCashOrderPayment) =>
          total + Number(payment.payAmount),
        0,
      ),
    [payments],
  );

  return (
    <div className={className}>
      <h2 className="text-lg font-medium text-gray-900 mb-6 pb-4 border-b">
        {t(titleLabel)}
      </h2>

      <div className="space-y-6">
        {payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                    {t(LOCALS.PVfpzNPNmJ)}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                    {t(LOCALS.QIXxrHAzgP)}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                    {t(LOCALS.payment_receipt_images)}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 border-b">
                    {t(LOCALS.MZYosCkjLE)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment, index) => {
                  const receiptKeys = payment.receiptKeys ?? [];

                  return (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        #{payment.orderId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {accountNameByCode[payment.payType] || payment.payType}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {receiptKeys.length > 0 ? (
                          <Image.PreviewGroup>
                            <div className="flex items-center gap-2">
                              {receiptKeys.map((imageKey) => (
                                <PrivateReceiptImage
                                  key={imageKey}
                                  imageKey={imageKey}
                                />
                              ))}
                            </div>
                          </Image.PreviewGroup>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                        {currencySymbol} {formatAmount(Number(payment.payAmount))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-6 pt-4 border-t-2 border-gray-200">
              <div
                className={`flex items-center justify-between py-3 px-4 rounded-lg border ${totalContainerClassName}`}
              >
                <span className="text-lg font-semibold text-gray-900">
                  {t(totalLabel)}
                </span>
                <span className={`text-xl font-bold ${totalAmountClassName}`}>
                  {currencySymbol} {formatAmount(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {t(emptyLabel)}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentRecordsCard;
